import React, { useEffect, useState } from "react";

import {
  Box,
  Typography,
  Paper,
  Chip,
  LinearProgress,
  CircularProgress
} from "@mui/material";

import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from "recharts";

import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import PlayCircleOutlineRoundedIcon from "@mui/icons-material/PlayCircleOutlineRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";

import { getProjectsOverview } from "../../services/reportService";
import uiConfig from "../../config/uiConfig";

const PALETTE = uiConfig.chartPalette;

const STATUS_META = {
  Active:    { color: "#16a34a", bg: "#f0fdf4", icon: <PlayCircleOutlineRoundedIcon sx={{ fontSize: 20 }} /> },
  Completed: { color: "#2563eb", bg: "#eff6ff", icon: <CheckCircleOutlineRoundedIcon sx={{ fontSize: 20 }} /> },
  Upcoming:  { color: "#b45309", bg: "#fef3c7", icon: <ScheduleRoundedIcon sx={{ fontSize: 20 }} /> }
};

function ProjectsOverviewReport({ departmentId = null, startDate = null, endDate = null }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getProjectsOverview(departmentId, startDate, endDate)
      .then(setData)
      .catch((error) => console.error("Projects Overview Error:", error))
      .finally(() => setLoading(false));
  }, [departmentId, startDate, endDate]);

  if (loading) {
    return (
      <Box height={330} display="flex" justifyContent="center" alignItems="center">
        <CircularProgress />
      </Box>
    );
  }

  const byStatus = data?.by_status || [];
  const byType = data?.by_type || [];
  const total = data?.total_projects || 0;

  const CustomDonutLabel = ({ cx, cy }) => (
    <>
      <text x={cx} y={cy - 8} textAnchor="middle" fill="#1e293b" fontSize={24} fontWeight={700}>
        {total}
      </text>
      <text x={cx} y={cy + 14} textAnchor="middle" fill="#94a3b8" fontSize={11}>
        total
      </text>
    </>
  );

  return (
    <Paper sx={{ p: 2.5, borderRadius: 3, border: "1px solid", borderColor: "divider", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
      <Typography sx={{ fontSize: 15, fontWeight: 700, color: "text.primary", mb: 2 }}>
        Projects Overview
      </Typography>

      {/* Status stat cards */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 1.5, mb: 2.5 }}>
        {byStatus.map((s) => {
          const meta = STATUS_META[s.name] || { color: PALETTE[0], bg: "#f1f5f9", icon: null };
          const pct = total > 0 ? Math.round((s.value / total) * 100) : 0;
          return (
            <Box key={s.name} sx={{ p: 1.5, borderRadius: 2, bgcolor: meta.bg, border: "1px solid", borderColor: "divider" }}>
              <Box display="flex" alignItems="center" gap={0.8} mb={0.5} sx={{ color: meta.color }}>
                {meta.icon}
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: meta.color }}>{s.name}</Typography>
              </Box>
              <Typography sx={{ fontSize: 26, fontWeight: 800, color: "text.primary", lineHeight: 1 }}>{s.value}</Typography>
              <LinearProgress
                variant="determinate"
                value={pct}
                sx={{ mt: 1, height: 4, borderRadius: 2, bgcolor: "#e2e8f0", "& .MuiLinearProgress-bar": { bgcolor: meta.color } }}
              />
              <Typography sx={{ fontSize: 10, color: "text.disabled", mt: 0.4 }}>{pct}% of total</Typography>
            </Box>
          );
        })}
      </Box>

      {/* Donut + Type bar side by side */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
        <Box>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: "text.secondary", mb: 0.5 }}>Status breakdown</Typography>
          <ResponsiveContainer width="100%" height={160}>
            <PieChart>
              <Pie
                data={byStatus}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={45}
                outerRadius={68}
                labelLine={false}
              >
                {byStatus.map((entry) => (
                  <Cell key={entry.name} fill={STATUS_META[entry.name]?.color || PALETTE[3]} />
                ))}
              </Pie>
              <Tooltip />
              <CustomDonutLabel cx={0} cy={0} />
            </PieChart>
          </ResponsiveContainer>
        </Box>

        <Box>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: "text.secondary", mb: 0.5 }}>By type</Typography>
          {byType.length === 0 ? (
            <Typography fontSize={12} color="text.secondary">No data</Typography>
          ) : (
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={byType} layout="vertical" margin={{ left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" allowDecimals={false} fontSize={11} />
                <YAxis type="category" dataKey="name" fontSize={11} width={60} />
                <Tooltip />
                <Bar dataKey="value" radius={[0, 6, 6, 0]} maxBarSize={20}>
                  {byType.map((entry, i) => (
                    <Cell key={entry.name} fill={PALETTE[i % PALETTE.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Box>
      </Box>
    </Paper>
  );
}

export default ProjectsOverviewReport;
